/**
 * Age chips for the size filter.
 *
 * The chips are a label on Shopify's own variant filter, not a filter of their
 * own: clicking "6-12 months" ticks the real `filter.v.option.size` inputs for
 * 6M, 9M, 12M and 6-12M, then hands off to Horizon's FacetsFormComponent. That
 * matters because Shopify applies size and availability to the SAME variant, so
 * routing through the native inputs is what keeps the age filter stock-accurate
 * - something a precomputed age metafield could never be.
 *
 * Everything downstream (URL state, section re-render, view transition, the
 * clear pills) is Horizon's, unchanged. This only decides which boxes are
 * ticked, and calls updateFilters exactly ONCE per click so a chip covering
 * five sizes still costs a single request.
 */
class PipAgeFacets extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = (event) => {
    const chip = event.target;
    if (!(chip instanceof HTMLInputElement) || !chip.dataset.sizes) return;

    const form = this.closest('form');
    const facetsForm = this.closest('facets-form-component');
    if (!form || !facetsForm) return;

    const param = this.dataset.param;
    // Trailing separator from the Liquid append leaves an empty final entry.
    const wanted = chip.dataset.sizes.split('~').filter(Boolean);
    const checked = chip.checked;

    let changed = false;
    for (const input of form.querySelectorAll('input[type="checkbox"]')) {
      if (input.name !== param || !wanted.includes(input.value)) continue;
      // A disabled value is one this collection cannot offer; ticking it would
      // send a filter that returns nothing.
      if (input.disabled || input.checked === checked) continue;
      input.checked = checked;
      changed = true;
    }

    if (!changed) return;

    // One call for the whole group. Dispatching change per input would fire a
    // request per size and race the section renders against each other.
    if (typeof facetsForm.updateFilters === 'function') facetsForm.updateFilters();
  };
}

if (!customElements.get('pip-age-facets')) {
  customElements.define('pip-age-facets', PipAgeFacets);
}
